'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { toast } from 'sonner';
import { Search, Plus, Upload, Download, Trash2, Tag, Users, UserCheck, UserX, AlertCircle, CheckSquare, Mail, X } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import Modal from '@/components/ui/Modal';
import EmptyState from '@/components/ui/EmptyState';
import { subscriberService, mailListService, type Subscriber, type MailList, type SubscriberStatus } from '@/lib/services/mailflowService';

const statusVariant: Record<SubscriberStatus, 'success' | 'neutral' | 'danger' | 'warning'> = {
  active: 'success',
  unsubscribed: 'neutral',
  bounced: 'danger',
  pending: 'warning',
};

export default function SubscriberManagementClient() {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [mailLists, setMailLists] = useState<MailList[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedList, setSelectedList] = useState('All Lists');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newListModalOpen, setNewListModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newList, setNewList] = useState('All Subscribers');
  const [newListName, setNewListName] = useState('');
  const [newListDesc, setNewListDesc] = useState('');
  const [newListColor, setNewListColor] = useState('#5b35e8');
  const [activeListTab, setActiveListTab] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [subs, lists] = await Promise.all([
        subscriberService.getAll(),
        mailListService.getAll(),
      ]);
      setSubscribers(subs);
      setMailLists(lists);
    } catch (err: any) {
      toast.error('Failed to load subscriber data');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return subscribers.filter((s) => {
      const matchSearch =
        s.email.toLowerCase().includes(search.toLowerCase()) ||
        s.firstName.toLowerCase().includes(search.toLowerCase()) ||
        s.lastName.toLowerCase().includes(search.toLowerCase());
      const matchList = selectedList === 'All Lists' || s.listName === selectedList;
      const matchStatus = statusFilter === 'all' || s.status === statusFilter;
      return matchSearch && matchList && matchStatus;
    });
  }, [subscribers, search, selectedList, statusFilter]);

  const getListCount = (listName: string) =>
    subscribers.filter((s) => s.listName === listName && s.status === 'active').length;

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((s) => s.id)));
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selected);
    try {
      await subscriberService.bulkDelete(ids);
      setSubscribers((prev) => prev.filter((s) => !selected.has(s.id)));
      toast.success(`${ids.length} subscribers removed`);
      setSelected(new Set());
    } catch {
      toast.error('Failed to remove subscribers');
    }
  };

  const handleAddSubscriber = async () => {
    if (!newEmail) return;
    const selectedMailList = mailLists.find((l) => l.name === newList);
    try {
      const newSub = await subscriberService.create({
        email: newEmail,
        firstName: newFirstName,
        lastName: newLastName,
        listName: newList,
        mailListId: selectedMailList?.id,
      });
      if (newSub) {
        setSubscribers((prev) => [newSub, ...prev]);
        toast.success('Subscriber added');
        setAddModalOpen(false);
        setNewEmail(''); setNewFirstName(''); setNewLastName('');
      }
    } catch {
      toast.error('Failed to add subscriber');
    }
  };

  const handleCreateList = async () => {
    if (!newListName.trim()) return;
    try {
      const created = await mailListService.create({
        name: newListName.trim(),
        description: newListDesc.trim() || 'No description',
        color: newListColor,
      });
      if (created) {
        setMailLists((prev) => [...prev, created]);
        toast.success(`"${created.name}" list created`);
        setNewListModalOpen(false);
        setNewListName(''); setNewListDesc(''); setNewListColor('#5b35e8');
      }
    } catch {
      toast.error('Failed to create list');
    }
  };

  const handleDeleteList = async (listId: string, listName: string) => {
    try {
      await mailListService.delete(listId);
      setMailLists((prev) => prev.filter((l) => l.id !== listId));
      toast.success(`"${listName}" list removed`);
      if (activeListTab === listId) setActiveListTab(null);
    } catch {
      toast.error('Failed to delete list');
    }
  };

  const handleDeleteSubscriber = async (id: string) => {
    try {
      await subscriberService.delete(id);
      setSubscribers((prev) => prev.filter((s) => s.id !== id));
      toast.success('Subscriber removed');
    } catch {
      toast.error('Failed to remove subscriber');
    }
  };

  const activeCount = subscribers.filter((s) => s.status === 'active').length;
  const bouncedCount = subscribers.filter((s) => s.status === 'bounced').length;
  const unsubCount = subscribers.filter((s) => s.status === 'unsubscribed').length;

  const listOptions = ['All Lists', ...mailLists.map((l) => l.name)];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Subscribers</h1>
          <p className="text-sm text-muted-foreground mt-1">{subscribers.length.toLocaleString()} total subscribers across {mailLists.length} lists</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => toast.info('Export started — CSV will download shortly')} className="btn-secondary">
            <Download size={15} /> Export CSV
          </button>
          <button onClick={() => toast.info('Import modal — backend integration point')} className="btn-secondary">
            <Upload size={15} /> Import
          </button>
          <button onClick={() => setAddModalOpen(true)} className="btn-primary">
            <Plus size={15} /> Add Subscriber
          </button>
        </div>
      </div>

      {/* List Health Bar */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center">
            <UserCheck size={18} className="text-green-600" />
          </div>
          <div>
            <p className="metric-label">Active</p>
            <p className="text-xl font-bold tabular-nums text-foreground">{activeCount.toLocaleString()}</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
            <UserX size={18} className="text-amber-600" />
          </div>
          <div>
            <p className="metric-label">Unsubscribed</p>
            <p className="text-xl font-bold tabular-nums text-foreground">{unsubCount.toLocaleString()}</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-3 ring-1 ring-red-200">
          <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
            <AlertCircle size={18} className="text-red-600" />
          </div>
          <div>
            <p className="metric-label">Bounced</p>
            <p className="text-xl font-bold tabular-nums text-foreground">{bouncedCount.toLocaleString()}</p>
            <p className="text-xs text-red-600">Clean list recommended</p>
          </div>
        </div>
      </div>

      {/* Mail Lists Section */}
      <div className="card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Mail size={16} className="text-primary" />
            <h2 className="text-sm font-semibold text-foreground">Mail Lists</h2>
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">{mailLists.length}</span>
          </div>
          <button onClick={() => setNewListModalOpen(true)} className="btn-primary text-xs px-3 py-1.5">
            <Plus size={13} /> New List
          </button>
        </div>
        {loading ? (
          <div className="p-8 flex items-center justify-center">
            <svg className="animate-spin w-6 h-6 text-primary" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="30 70" />
            </svg>
          </div>
        ) : (
          <div className="p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {mailLists.map((ml) => {
              const count = getListCount(ml.name);
              const isActive = activeListTab === ml.id;
              return (
                <div
                  key={ml.id}
                  onClick={() => {
                    setActiveListTab(isActive ? null : ml.id);
                    setSelectedList(isActive ? 'All Lists' : ml.name);
                  }}
                  className={`relative group rounded-xl border p-4 cursor-pointer transition-all duration-150 ${
                    isActive
                      ? 'border-primary/40 bg-primary/5 shadow-sm'
                      : 'border-border bg-card hover:border-primary/30 hover:bg-muted/30'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${ml.color}18` }}
                    >
                      <Mail size={15} style={{ color: ml.color }} />
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteList(ml.id, ml.name); }}
                      className="opacity-0 group-hover:opacity-100 w-6 h-6 rounded flex items-center justify-center hover:bg-red-50 transition-all duration-150"
                      title="Delete list"
                    >
                      <X size={12} className="text-red-400" />
                    </button>
                  </div>
                  <p className="text-sm font-semibold text-foreground leading-tight mb-0.5 truncate">{ml.name}</p>
                  <p className="text-xs text-muted-foreground truncate mb-2">{ml.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold tabular-nums" style={{ color: ml.color }}>{count.toLocaleString()}</span>
                    <span className="text-xs text-muted-foreground">active</span>
                  </div>
                  {isActive && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-b-xl" style={{ backgroundColor: ml.color }} />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Filters row */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-card flex-1 max-w-sm">
          <Search size={14} className="text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by email or name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none w-full"
          />
        </div>
        <select
          value={selectedList}
          onChange={(e) => { setSelectedList(e.target.value); setActiveListTab(null); }}
          className="input-base w-auto"
        >
          {listOptions.map((l) => <option key={`list-sel-${l}`} value={l}>{l}</option>)}
        </select>
        <div className="flex items-center gap-2">
          {(['all', 'active', 'unsubscribed', 'bounced', 'pending'] as const).map((s) => (
            <button
              key={`sstatus-${s}`}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 ${
                statusFilter === s
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-secondary hover:text-primary'
              }`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk bar */}
      {selected.size > 0 && (
        <div className="slide-up flex items-center gap-3 px-4 py-3 rounded-xl bg-primary/5 border border-primary/20">
          <CheckSquare size={16} className="text-primary" />
          <span className="text-sm font-semibold text-primary">{selected.size} selected</span>
          <div className="flex-1" />
          <button onClick={handleBulkDelete} className="btn-danger text-xs px-3 py-1.5">Remove Selected</button>
          <button onClick={() => toast.info('Tag assignment — backend integration point')} className="btn-secondary text-xs px-3 py-1.5">
            <Tag size={13} /> Add Tag
          </button>
          <button onClick={() => setSelected(new Set())} className="btn-ghost text-xs">Clear</button>
        </div>
      )}

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <svg className="animate-spin w-8 h-8 text-primary" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="30 70" />
              </svg>
              <p className="text-sm text-muted-foreground">Loading subscribers…</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={selected.size === filtered.length && filtered.length > 0}
                      onChange={toggleAll}
                      className="w-4 h-4 rounded border-border accent-primary cursor-pointer"
                    />
                  </th>
                  {['Email', 'Name', 'List', 'Status', 'Tags', 'Source', 'Joined'].map((col) => (
                    <th key={`sub-th-${col}`} className="px-4 py-3 text-left metric-label">{col}</th>
                  ))}
                  <th className="px-4 py-3 text-left metric-label w-20">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9}>
                      <EmptyState
                        icon={Users}
                        title="No subscribers found"
                        description="Try adjusting your filters or add a new subscriber to get started."
                        actionLabel="Add Subscriber"
                        onAction={() => setAddModalOpen(true)}
                      />
                    </td>
                  </tr>
                ) : (
                  filtered.map((s) => (
                    <tr key={s.id} className={`border-b border-border last:border-0 hover:bg-muted/30 transition-colors duration-100 ${selected.has(s.id) ? 'bg-primary/5' : ''}`}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected.has(s.id)}
                          onChange={() => toggleSelect(s.id)}
                          className="w-4 h-4 rounded border-border accent-primary cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-foreground">{s.email}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground">{s.firstName} {s.lastName}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{s.listName}</td>
                      <td className="px-4 py-3">
                        <Badge variant={statusVariant[s.status]}>
                          {s.status.charAt(0).toUpperCase() + s.status.slice(1)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 flex-wrap">
                          {s.tags?.slice(0, 2).map((tag) => (
                            <span key={`${s.id}-tag-${tag}`} className="badge bg-secondary text-secondary-foreground">{tag}</span>
                          ))}
                          {(!s.tags || s.tags.length === 0) && <span className="text-xs text-muted-foreground">—</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{s.source}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                        {new Date(s.joinedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleDeleteSubscriber(s.id)}
                          title="Remove subscriber"
                          className="w-7 h-7 rounded flex items-center justify-center hover:bg-red-50 transition-all duration-150"
                        >
                          <Trash2 size={13} className="text-red-500" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-border bg-muted/20">
            <p className="text-xs text-muted-foreground">
              Showing {filtered.length} of {subscribers.length} subscribers
            </p>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Per page:</span>
              <select className="text-xs border border-border rounded px-2 py-1 bg-card text-foreground">
                <option>25</option>
                <option>50</option>
                <option>100</option>
              </select>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4].map((p) => (
                  <button key={`sub-page-${p}`} className={`w-7 h-7 rounded text-xs font-medium transition-all duration-150 ${p === 1 ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Subscriber Modal */}
      <Modal open={addModalOpen} onClose={() => setAddModalOpen(false)} title="Add Subscriber" size="sm">
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-base" htmlFor="newFirst">First Name</label>
              <input id="newFirst" type="text" value={newFirstName} onChange={(e) => setNewFirstName(e.target.value)} className="input-base" placeholder="Priya" />
            </div>
            <div>
              <label className="label-base" htmlFor="newLast">Last Name</label>
              <input id="newLast" type="text" value={newLastName} onChange={(e) => setNewLastName(e.target.value)} className="input-base" placeholder="Sharma" />
            </div>
          </div>
          <div>
            <label className="label-base" htmlFor="newEmailField">Email Address</label>
            <input id="newEmailField" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="input-base" placeholder="subscriber@example.com" />
          </div>
          <div>
            <label className="label-base" htmlFor="newListSel">Add to List</label>
            <select id="newListSel" value={newList} onChange={(e) => setNewList(e.target.value)} className="input-base">
              {mailLists.map((l) => <option key={`modal-list-${l.id}`} value={l.name}>{l.name}</option>)}
            </select>
          </div>
          <div className="flex gap-3 justify-end pt-2 border-t border-border">
            <button onClick={() => setAddModalOpen(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleAddSubscriber} className="btn-primary">Add Subscriber</button>
          </div>
        </div>
      </Modal>

      {/* New Mail List Modal */}
      <Modal open={newListModalOpen} onClose={() => setNewListModalOpen(false)} title="Create Mail List" size="sm">
        <div className="p-6 space-y-4">
          <div>
            <label className="label-base" htmlFor="mlName">List Name</label>
            <input id="mlName" type="text" value={newListName} onChange={(e) => setNewListName(e.target.value)} className="input-base" placeholder="e.g. Newsletter Subscribers" />
          </div>
          <div>
            <label className="label-base" htmlFor="mlDesc">Description</label>
            <input id="mlDesc" type="text" value={newListDesc} onChange={(e) => setNewListDesc(e.target.value)} className="input-base" placeholder="Brief description of this list" />
          </div>
          <div>
            <label className="label-base" htmlFor="mlColor">List Color</label>
            <div className="flex items-center gap-3 mt-1">
              <input id="mlColor" type="color" value={newListColor} onChange={(e) => setNewListColor(e.target.value)} className="w-10 h-10 rounded-lg border border-border cursor-pointer bg-card p-1" />
              <div className="flex items-center gap-2">
                {['#5b35e8', '#ff5c2b', '#059669', '#d97706', '#0891b2', '#7c3aed', '#e11d48'].map((c) => (
                  <button
                    key={c}
                    onClick={() => setNewListColor(c)}
                    className={`w-6 h-6 rounded-full transition-all duration-150 ${newListColor === c ? 'ring-2 ring-offset-2 ring-primary scale-110' : 'hover:scale-105'}`}
                    style={{ backgroundColor: c }}
                    title={c}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="flex gap-3 justify-end pt-2 border-t border-border">
            <button onClick={() => setNewListModalOpen(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleCreateList} disabled={!newListName.trim()} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
              Create List
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}